import React, { useCallback, useEffect, useRef, useState } from "react";
import * as Mui from "@mui/material";
import moment from "moment";
import { useSelector } from "react-redux";
import { CalendarMonthOutlined, CurrencyRupeeOutlined, PlaceOutlined, ReceiptLongOutlined, TuneOutlined } from "@mui/icons-material";
import { showToast } from "services/utils/Status";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import { RequestInfoBanner } from "components/DesignSystem/RequestInfoBanner";
import { RequestStatusChip } from "components/DesignSystem/RequestStatusChip";
import { RequestDetailsSection } from "components/DesignSystem/RequestDetailsSection";
import { detailAccentPalette } from "components/DesignSystem/RequestDetailField";
import { PrimaryButton, SecondaryButton } from "components/CustomComponents/DesignSystemButtons";
import { useSavedViewSelector } from "components/CustomComponents/useSavedViewSelector";
import { Loader } from "components/Loader";
import { designColors, designShell, designType, designWeights } from "services/constants/designTokens";
import { ptRegisterHeader } from "pages/PayRoll/PayRollHeaders";
import { panelBodySx } from "pages/PayRoll/PayInputs/Shared/formUi";
import { EmptyRow, SimpleTable, ToneChip, tableCellSx } from "pages/PayRoll/Salary/Shared/salaryUi";
import { FilterSelect, RecordHeaderCard, StatutoryList, initialsOf } from "pages/PayRoll/Shared/statutoryUi";
import { downloadPtRegister, fetchPtCalculation, fetchPtCalculationHistory, fetchPtRegister } from "./ptReportsApi";

// PT Register (PT4) from GET reports/register/: one row per employee of
// the finalised month — search, status filter, paging and downloads go to
// the server. The footer total (totals.text) follows only the site and
// department filters, so it always equals the payroll PT total. A row
// opens the PT Calculation drawer (PT4a): GET reports/register/<id>/ for
// This Month (ready sentences) and .../history/ for every finalised month.

const { purple, indigo, green, orange } = detailAccentPalette;
const TONE_COLORS = { green: "#12B76A", slate: "#6B7280", blue: "#2563EB", red: "#DC2626", purple: "#6D5DF6", amber: "#F0954A" };
const ALL = "all";
const PAGE_SIZE = 50;
const EXPORT_PAGE_SIZE = 200;
const SEARCH_DELAY = 400;

export const STATUS_FILTERS = [
  { value: ALL, label: "All statuses" },
  { value: "deducted", label: "Deducted" },
  { value: "in_last_month", label: "In last month" },
  { value: "below_slab", label: "Below slab" },
  { value: "exempt", label: "Exempt" },
  { value: "yearly_limit", label: "Yearly limit" },
  { value: "site_not_set_up", label: "Site not set up" },
  { value: "held", label: "Held" },
  { value: "pay_cycle_not_supported", label: "Pay cycle not supported" },
  { value: "refund", label: "Refund" },
];

const money = (value) => (value == null ? "—" : `₹${Number(value).toLocaleString("en-IN")}`);
const num = (value) => (value == null ? "—" : Number(value).toLocaleString("en-IN"));

const CalculationPanel = ({ open, domain, companyId, row, month, onClose }) => {
  const [tab, setTab] = useState("month");
  const [detail, setDetail] = useState({ loading: true, error: "", data: null });
  const [history, setHistory] = useState({ loading: false, error: "", rows: null });
  const employeeId = row?.id;

  useEffect(() => {
    if (open) setTab("month");
  }, [open, employeeId]);

  useEffect(() => {
    if (!open || employeeId == null) return undefined;
    const controller = new AbortController();
    setDetail({ loading: true, error: "", data: null });
    setHistory({ loading: false, error: "", rows: null });
    fetchPtCalculation({ domain, companyId, employeeId, month, signal: controller.signal })
      .then((data) => setDetail({ loading: false, error: "", data }))
      .catch((error) => {
        if (controller.signal.aborted) return;
        // 404: nothing finalised for this employee this month — close with a toast.
        if (error.status === 404) {
          showToast("error", error.message || "No finalised PT for this employee in this month.");
          onClose();
          return;
        }
        setDetail({ loading: false, error: error.message || "Could not load the PT calculation.", data: null });
      });
    return () => controller.abort();
    // onClose changes every render; the drawer reloads only for a new row / month.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, domain, companyId, employeeId, month]);

  // History loads the first time its tab opens.
  useEffect(() => {
    if (!open || tab !== "history" || employeeId == null || history.rows || history.loading) return undefined;
    const controller = new AbortController();
    setHistory({ loading: true, error: "", rows: null });
    fetchPtCalculationHistory({ domain, companyId, employeeId, signal: controller.signal })
      .then((rows) => setHistory({ loading: false, error: "", rows }))
      .catch((error) => {
        if (!controller.signal.aborted) setHistory({ loading: false, error: error.message || "Could not load the history.", rows: null });
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tab, domain, companyId, employeeId]);

  if (!row) return null;
  const data = detail.data;
  const settlement = data?.parts.some((part) => part.part === "SETTLEMENT");

  return (
    <GlobalSidePanel
      open={open}
      onClose={onClose}
      title="PT Calculation"
      subtitle={`${data?.month_label || moment(`${month}-01`).format("MMM YYYY")} · from finalised payroll`}
      bodySx={panelBodySx}
      footer={
        <Mui.Stack direction="row" justifyContent="flex-end" sx={{ width: "100%" }}>
          <PrimaryButton onClick={onClose}>Close</PrimaryButton>
        </Mui.Stack>
      }
    >
      <Mui.Stack spacing={2}>
        <RecordHeaderCard
          avatar={initialsOf(data?.employee.name || row.name)}
          title={data?.employee.name || row.name}
          chip={<RequestStatusChip status={data?.status_label || row.status_label} colorCode={TONE_COLORS[data?.status_tone || row.status_tone]} />}
          meta={[data?.employee.code || row.emp_id, data?.parts[0]?.site || row.site_name].filter(Boolean).join(" · ")}
          tone={(data?.status_tone || row.status_tone) === "red" ? "danger" : row.status === "EXEMPT" ? "slate" : "teal"}
        />
        <Mui.Tabs value={tab} onChange={(event, next) => setTab(next)} sx={designShell.panelTabs}>
          <Mui.Tab value="month" label="This Month" />
          <Mui.Tab value="history" label="History" />
        </Mui.Tabs>
        {tab === "month" ? (
          detail.loading ? (
            <Loader info="Loading..." minHeight="30vh" />
          ) : detail.error ? (
            <RequestInfoBanner tone="danger" title="Couldn't load the PT calculation." body={detail.error} />
          ) : data ? (
            <>
              <RequestDetailsSection
                title="RULE USED"
                fields={[
                  { icon: PlaceOutlined, label: "Site", value: data.rule_used.site || "—", helper: data.rule_used.site_note || undefined, accent: purple, multiline: true },
                  ...(data.rule_used.period ? [{ icon: CalendarMonthOutlined, label: "Period", value: data.rule_used.period, accent: orange }] : []),
                  ...(data.rule_used.how_collected ? [{ icon: TuneOutlined, label: "How PT is collected", value: data.rule_used.how_collected, accent: indigo }] : []),
                ]}
              />
              {data.wage.monthly ? (
                <RequestDetailsSection
                  title="WAGE"
                  fields={[{ icon: CurrencyRupeeOutlined, label: "Monthly wage", value: data.wage.monthly, helper: data.wage.note || undefined, accent: green }]}
                />
              ) : null}
              {data.lines.length ? (
                <Mui.Box sx={{ p: 2, border: `1px solid ${designColors.slate200}`, borderRadius: 2, backgroundColor: designColors.white }}>
                  <Mui.Typography sx={{ ...designType.sectionHeading, mb: 1 }}>PT for this month</Mui.Typography>
                  <Mui.Stack spacing={0.75}>
                    {data.lines.map((line, index) => (
                      <Mui.Typography key={`${index}-${line}`} sx={designType.tableBodyCell}>
                        {line}
                      </Mui.Typography>
                    ))}
                  </Mui.Stack>
                </Mui.Box>
              ) : null}
              {settlement ? (
                <SimpleTable
                  fit
                  heads={[
                    { label: "Part", width: 120 },
                    { label: "Site", width: 150 },
                    { label: "Period", width: 160 },
                    { label: "PT (₹)", width: 80, align: "right" },
                  ]}
                >
                  {data.parts.map((part, index) => (
                    <Mui.TableRow key={`${part.part}-${index}`}>
                      <Mui.TableCell sx={tableCellSx}>{part.part === "SETTLEMENT" ? `Settlement${part.settle_label ? ` · ${part.settle_label}` : ""}` : "Regular"}</Mui.TableCell>
                      <Mui.TableCell sx={tableCellSx}>{part.site || "—"}</Mui.TableCell>
                      <Mui.TableCell sx={tableCellSx}>{part.period_label || "—"}</Mui.TableCell>
                      <Mui.TableCell align="right" sx={tableCellSx}>{num(part.amount)}</Mui.TableCell>
                    </Mui.TableRow>
                  ))}
                </SimpleTable>
              ) : null}
              <RequestDetailsSection
                title="PAYSLIP"
                fields={[{ icon: ReceiptLongOutlined, label: "Professional Tax", value: data.payslip || money(data.pt_this_month), accent: green }]}
              />
              {data.warnings.length ? <RequestInfoBanner tone="warning" body={`${data.warnings.join(" · ")} — see the Validation tab.`} /> : null}
            </>
          ) : null
        ) : history.loading ? (
          <Loader info="Loading..." minHeight="30vh" />
        ) : history.error ? (
          <RequestInfoBanner tone="danger" title="Couldn't load the history." body={history.error} />
        ) : (
          <SimpleTable
            fit
            heads={[
              { label: "Month", width: 90 },
              { label: "Site", width: 140 },
              { label: "Period", width: 150 },
              { label: "Wage (₹)", width: 90, align: "right" },
              { label: "PT (₹)", width: 70, align: "right" },
              { label: "Status", width: 150 },
            ]}
          >
            {history.rows?.length ? (
              history.rows.map((entry) => (
                <Mui.TableRow key={entry.id}>
                  <Mui.TableCell sx={tableCellSx}>
                    {entry.label}
                    {entry.settlement ? " · settlement" : ""}
                  </Mui.TableCell>
                  <Mui.TableCell sx={tableCellSx}>{entry.site || "—"}</Mui.TableCell>
                  <Mui.TableCell sx={tableCellSx}>{entry.period_label || "—"}</Mui.TableCell>
                  <Mui.TableCell align="right" sx={tableCellSx}>{num(entry.monthly_wage)}</Mui.TableCell>
                  <Mui.TableCell align="right" sx={tableCellSx}>{num(entry.pt_amount)}</Mui.TableCell>
                  <Mui.TableCell sx={tableCellSx}>
                    <ToneChip label={entry.status_label} tone={entry.status_tone} />
                  </Mui.TableCell>
                </Mui.TableRow>
              ))
            ) : (
              <EmptyRow colSpan={6} text="No finalised PT yet." />
            )}
          </SimpleTable>
        )}
      </Mui.Stack>
    </GlobalSidePanel>
  );
};

export const PtRegisterTab = ({ domain, companyId, month, monthLabel, siteId, departmentId, initialStatus = ALL }) => {
  const { userDetails, topbarCompanyName } = useSelector((state) => state.tokenReducer);
  const [status, setStatus] = useState(initialStatus);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [reloadKey, setReloadKey] = useState(0);
  const [list, setList] = useState({ loading: true, error: "", rows: [], count: 0, total_text: "", note: "" });
  const [viewId, setViewId] = useState(null);
  const firstSearch = useRef(true);

  const { visibleHeaders, titleOverride } = useSavedViewSelector({
    domain,
    userId: userDetails?.id,
    storageKey: "pt_register_saved_views",
    fullHeaders: ptRegisterHeader,
  });

  useEffect(() => {
    if (firstSearch.current) {
      firstSearch.current = false;
      return undefined;
    }
    const timer = setTimeout(() => {
      setQuery(search.trim());
      setPage(0);
    }, SEARCH_DELAY);
    return () => clearTimeout(timer);
  }, [search]);

  // A new site / department filter starts again from page 1.
  useEffect(() => setPage(0), [siteId, departmentId]);

  const statusValue = status === ALL ? undefined : status;
  useEffect(() => {
    const controller = new AbortController();
    setList((current) => ({ ...current, loading: true, error: "" }));
    fetchPtRegister({ domain, companyId, month, siteId, departmentId, status: statusValue, search: query, page: page + 1, pageSize, signal: controller.signal })
      .then((data) => setList({ loading: false, error: "", ...data }))
      .catch((error) => {
        if (!controller.signal.aborted) setList((current) => ({ ...current, loading: false, error: error.message || "Could not load the PT register." }));
      });
    return () => controller.abort();
  }, [domain, companyId, month, siteId, departmentId, statusValue, query, page, pageSize, reloadKey]);

  const exportRows = useCallback(async () => {
    const all = [];
    for (let next = 1; ; next += 1) {
      // eslint-disable-next-line no-await-in-loop
      const data = await fetchPtRegister({ domain, companyId, month, siteId, departmentId, status: statusValue, search: query, page: next, pageSize: EXPORT_PAGE_SIZE });
      all.push(...data.rows);
      if (!data.rows.length || all.length >= data.count) return all;
    }
  }, [domain, companyId, month, siteId, departmentId, statusValue, query]);

  // Downloads come from the server (same filters, every matching row).
  const downloadFile = async (format) => {
    try {
      await downloadPtRegister({ domain, companyId, month, siteId, departmentId, status: statusValue, search: query, format });
    } catch (error) {
      showToast("error", error.message || "Couldn't download the register.");
    }
    return true;
  };

  const renderCell = (row, key) => {
    switch (key) {
      case "name":
        return (
          <Mui.Stack direction="row" spacing={1} alignItems="center">
            <Mui.Avatar sx={{ width: 28, height: 28, fontSize: 11, fontWeight: 700, backgroundColor: designColors.slate100, color: designColors.slate700 }}>
              {initialsOf(row.name)}
            </Mui.Avatar>
            <Mui.Typography sx={{ ...designType.tableBodyCell, fontWeight: designWeights.medium, whiteSpace: "nowrap" }}>{row.name}</Mui.Typography>
          </Mui.Stack>
        );
      case "monthly_wage":
      case "pt":
        return num(row[key]);
      case "collected":
        // Monthly sites have no running period.
        return row.collected == null || row.how_collected === "Monthly" ? "—" : num(row.collected);
      case "status_label":
        return (
          <Mui.Stack direction="row" spacing={0.5} alignItems="center">
            <ToneChip label={row.status_label} tone={row.status_tone} />
            {row.has_settlement ? <Mui.Typography sx={{ ...designType.captionText, whiteSpace: "nowrap" }}>+ settlement</Mui.Typography> : null}
          </Mui.Stack>
        );
      default:
        return row[key] || "—";
    }
  };

  const viewed = list.rows.find((row) => row.id === viewId);
  return (
    <>
      <Mui.Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mb: 1.5 }}>
        <FilterSelect
          label="Status"
          value={status}
          onChange={(value) => {
            setStatus(value);
            setPage(0);
          }}
          options={STATUS_FILTERS}
        />
      </Mui.Stack>
      {list.error ? (
        <Mui.Box sx={{ mb: 1.5 }}>
          <RequestInfoBanner
            tone="danger"
            title="Couldn't load the PT register."
            body={list.error}
            action={<SecondaryButton onClick={() => setReloadKey((key) => key + 1)} sx={{ whiteSpace: "nowrap" }}>Retry</SecondaryButton>}
          />
        </Mui.Box>
      ) : null}
      {list.note ? (
        <Mui.Box sx={{ mb: 1.5 }}>
          <RequestInfoBanner body={list.note} />
        </Mui.Box>
      ) : null}
      <StatutoryList
        title={`PT Register ${list.month_label || monthLabel}`}
        headers={visibleHeaders}
        titleOverride={titleOverride}
        rows={list.rows}
        loading={list.loading}
        renderCell={renderCell}
        onRowClick={(row) => setViewId(row.id)}
        selectedKey={viewId}
        itemLabel="employees"
        searchPlaceholder="Search name or ID"
        metadata={{ "Company Name": topbarCompanyName || "N/A", "Payroll Month": list.month_label || monthLabel, "Report Generated On": moment().format("DD/MM/YYYY HH:mm:ss") }}
        server={{
          search,
          onSearchChange: setSearch,
          page,
          rowsPerPage: pageSize,
          count: list.count,
          onPageChange: setPage,
          onRowsPerPageChange: (size) => {
            setPageSize(size);
            setPage(0);
          },
          exportRows,
          downloadFile,
        }}
      />
      {list.total_text ? (
        <Mui.Box sx={{ ...designShell.sectionCard, mt: 1.5, px: 2, py: 1.25, textAlign: { sm: "right" } }}>
          <Mui.Typography sx={{ ...designType.tableBodyCell, fontWeight: designWeights.semibold }}>{list.total_text}</Mui.Typography>
        </Mui.Box>
      ) : null}
      <Mui.Box sx={{ mt: 1.5 }}>
        <RequestInfoBanner body="Click a row to see how the PT was worked out. Collected this period = PT taken from payslips since the period started; the last month of the period deducts whatever is still due. “+ settlement”: this month also closed an earlier period (site move, exemption or leaver)." />
      </Mui.Box>
      <CalculationPanel open={viewId != null && Boolean(viewed)} domain={domain} companyId={companyId} row={viewed} month={month} onClose={() => setViewId(null)} />
    </>
  );
};
